import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { useSelector, useDispatch } from 'react-redux';

import { setIsRefetch } from '../../../Slices/salesSlice';
import { InputField, SelectInput, AntdButton } from '../../../blueplanitUtils/InputField';
import { getOptionsByPath } from '../../../utilsdata';
import { findTimeZoneOption } from '../../../utils/timeZone';
import { AGENCY_INFORMATION, TIMEZONE } from '../../../Dropdownmanager';

const AgencyInformationForm = ({
  handleStartButtonClick,
  handleStop,
  task,
  CurrentTask,
  id,
  leadID,
}) => {
  const { values, errors, touched, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();

  const isRefetch = useSelector((state) => state.sales.isRefetch);
  const WorkingId = useSelector((state) => state?.timer?.requestId);
  const NestedDatabucket = useSelector((state) => state.dataBucket.NestedDatabucket);
  const [agencyName, setAgencyName] = useState('');
  const [dba, setDba] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [mailingAddress, setMailingAddress] = useState('');
  const [state, setState] = useState('');
  const [webURL, setWebURL] = useState('');
  const [contactInfoURL, setContactInfoURL] = useState('');
  const [linkedinURL, setLinkedinURL] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const initializedRef = useRef(false);
  const syncTimeoutRef = useRef(null);
  const userChangesRef = useRef({}); // Store user changes
  const lastFormikValuesRef = useRef(null); // Track last known Formik values

  // Initialize on mount only
  useEffect(() => {
    if (!initializedRef.current && values) {
      setAgencyName(values.agencyName || '');
      setDba(values.dba || '');
      setAddress(values.address || '');
      setCity(values.city || '');
      setState(values.state || '');
      setWebURL(values.webURL || '');
      setMailingAddress(values.mailingAddress || '');
      setContactInfoURL(values.contactInfoURL || '');
      setLinkedinURL(values.linkedinURL || '');
      setTimeZone(values.timeZone || '');
      lastFormikValuesRef.current = values;
      initializedRef.current = true;
    }
  }, [values]);

  // Detect Formik reinitialization and preserve user changes
  useEffect(() => {
    if (!initializedRef.current || !lastFormikValuesRef.current) return;

    const fieldsToCheck = [
      'agencyName', 'dba', 'address', 'city', 'mailingAddress',
      'state', 'webURL', 'contactInfoURL', 'linkedinURL', 'timeZone'
    ];

    // Check if Formik values changed externally (reinitialization)
    const formikReinitialized = fieldsToCheck.some(
      field => values[field] !== lastFormikValuesRef.current[field]
    );

    if (formikReinitialized) {
      console.log('Formik reinitialized detected, preserving user changes');

      // Apply user changes on top of new Formik values
      const hasUserChanges = Object.keys(userChangesRef.current).length > 0;

      if (hasUserChanges) {
        // Immediately apply user changes to new Formik values
        Object.entries(userChangesRef.current).forEach(([field, value]) => {
          setFieldValue(field, value, false);
        });
        console.log('User changes preserved:', userChangesRef.current);
      } else {
        // No user changes, update local state with new backend values
        setAgencyName(values.agencyName || '');
        setDba(values.dba || '');
        setAddress(values.address || '');
        setCity(values.city || '');
        setState(values.state || '');
        setWebURL(values.webURL || '');
        setMailingAddress(values.mailingAddress || '');
        setContactInfoURL(values.contactInfoURL || '');
        setLinkedinURL(values.linkedinURL || '');
        setTimeZone(values.timeZone || '');
      }

      lastFormikValuesRef.current = values;
    }
  }, [values, setFieldValue]);

  // Handle refetch flag
  useEffect(() => {
    if (isRefetch) {
      dispatch(setIsRefetch(false));
    }
  }, [isRefetch, dispatch]);

  // Debounced sync to Formik
  useEffect(() => {
    if (!initializedRef.current) return;

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      Object.entries(userChangesRef.current).forEach(([field, value]) => {
        if (values[field] !== value) {
          setFieldValue(field, value, false);
        }
      });
    }, 100);

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [agencyName, dba, address, city, mailingAddress, state, webURL, contactInfoURL, linkedinURL, timeZone, setFieldValue, values]);

  // Unified input handler
  const handleInputChange = useCallback((fieldName, value) => {
    // Store user change
    userChangesRef.current[fieldName] = value;

    // Update local state
    switch (fieldName) {
      case 'agencyName':
        setAgencyName(value);
        break;
      case 'dba':
        setDba(value);
        break;
      case 'address':
        setAddress(value);
        break;
      case 'city':
        setCity(value);
        break;
      case 'mailingAddress':
        setMailingAddress(value);
        break;
      case 'state':
        setState(value);
        break;
      case 'webURL':
        setWebURL(value);
        break;
      case 'contactInfoURL':
        setContactInfoURL(value);
        break;
      case 'linkedinURL':
        setLinkedinURL(value);
        break;
      case 'timeZone':
        setTimeZone(value);
        break;
      default:
        break;
    }
  }, []);

  // Flush pending updates immediately
  const flushPendingUpdates = useCallback(() => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }

    Object.entries(userChangesRef.current).forEach(([field, value]) => {
      if (values[field] !== value) {
        setFieldValue(field, value, false);
      }
    });
  }, [values, setFieldValue]);

  // Expose flush function for parent
  useEffect(() => {
    const formElement = document.querySelector('[data-agency-form="true"]');
    if (formElement) {
      formElement._flushPendingUpdates = flushPendingUpdates;
    }
  }, [flushPendingUpdates]);

  // Memoized timezone options
  const timezoneOptions = useMemo(
    () => getOptionsByPath(NestedDatabucket, [AGENCY_INFORMATION, TIMEZONE]),
    [NestedDatabucket]
  );

  const showStopButton = id !== undefined && WorkingId === leadID && task === CurrentTask;
  const selectWidth =
    window.innerWidth >= 1600 ? "320px" :       // Ultra-wide screens
      window.innerWidth >= 1440 ? "220px" :       // Large screens
        "192px";

  return (
    <div className="lead-info" data-agency-form="true">

      <Typography.Title level={5} className="title-row">
        Agency Information
        {id !== undefined && (
          showStopButton ? (
            <AntdButton label={<i className="fa-solid fa-stopwatch"></i>} color="danger" size="small" variant="solid" onClick={handleStop} />
          ) : (
            <AntdButton label={<i className="fa-solid fa-stopwatch"></i>} color="primary" size="small" variant="solid" onClick={handleStartButtonClick} />
          )
        )}
      </Typography.Title>

      <div>
        <div className="agency-card">

          <Row gutter={8}>

            {/* Name of Agency */}
            <Col span={24}>
              <div className="flex-row">

                {/* Name of the Agency */}
                <div className="flex-row" style={{ width: "58%" }}>
                  <label className="form-label">Agency</label>
                  <InputField
                    name="agencyName"
                    value={agencyName}
                    className="form-input"
                    onChange={(e) =>
                      handleInputChange("agencyName", e.target.value)
                    }
                    error={touched.agencyName && errors.agencyName}
                    required
                    copyable
                  />
                </div>

                {/* DBA */}
                <div className="flex-row flex-25">
                  <label className="form-label" style={{ minWidth: 30, marginTop: "10px" }}>DBA</label>
                  <InputField
                    name="dba"
                    value={dba}
                    className="form-input"
                    onChange={(e) => handleInputChange("dba", e.target.value)}
                    copyable
                  />
                </div>

              </div>
            </Col>


            {/* Physical Address */}
            <Col span={24}>
              <div className="form-row" style={{ marginTop: "0px" }}>
                <label className="form-label">Physical Address</label>
                <InputField
                  name="address"
                  value={address}
                  className="form-input"
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  copyable
                />
              </div>
            </Col>

            {/* Mailing Address */}
            <Col span={24}>
              <div className="form-row">
                <label className="form-label">Mailing Address</label>
                <InputField
                  name="mailingAddress"
                  value={mailingAddress}
                  className="form-input"
                  onChange={(e) =>
                    handleInputChange("mailingAddress", e.target.value)
                  }
                  copyable
                />
              </div>
            </Col>

            {/* City + State */}
            <Col span={24}>
              <div className="flex-row" >

                {/* City */}
                <div className="flex-row" style={{ width: "58%" }}>
                  <label className="form-label">City</label>
                  <InputField
                    name="city"
                    value={city}
                    className="form-input"
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    copyable
                  />
                </div>

                {/* State */}
                <div className="flex-row flex-25">
                  <label className="form-label" style={{ minWidth: 30, marginTop: "10px" }}>State</label>
                  <InputField
                    name="state"
                    value={state}
                    className="form-input"
                    onChange={(e) => handleInputChange("state", e.target.value)}
                    copyable
                  />
                </div>

              </div>
            </Col>
                      <Col span={24}>
              <div className="flex-row" >

                {/* Website */}
                <div className="flex-row" style={{ width: "58%" ,  marginTop:'-10px' }}>
                  <label className="form-label">Website</label>
                  <InputField
                    name="webURL"
                    value={webURL}
                    className="form-input"
                    onChange={(e) => handleInputChange("webURL", e.target.value)}
                    copyable
                  />
                </div>

                {/* Zone */}
                <div className="flex-row flex-25">
                  <label className="form-label" style={{ minWidth: 30 }}>Zone</label>
                    <SelectInput
                    style={{ width: selectWidth ,  marginTop: "10px"}}
                    dropdownStyle={{ borderRadius: "6px" }}
                    name="timeZone"
                    value={findTimeZoneOption(timeZone, timezoneOptions)}
                    onChange={(value) => handleInputChange("timeZone", value)}
                    options={timezoneOptions}
                  />           
                </div>

              </div>
            </Col>

            {/* Contact Info URL */}
            <Col span={24} style={{ marginTop:'-10px'}}>
              <div className="form-row">
                <label className="form-label">Contact URL</label>
                <InputField
                  name="contactInfoURL"
                  value={contactInfoURL}
                  className="form-input"
                  onChange={(e) =>
                    handleInputChange("contactInfoURL", e.target.value)
                  }
                  copyable
                />
              </div>
            </Col>

            {/* LinkedIn URL */}
            <Col span={24}>
              <div className="form-row">
                <label className="form-label">LinkedIn</label>
                <InputField
                  name="linkedinURL"
                  value={linkedinURL}
                  className="form-input"
                  onChange={(e) =>
                    handleInputChange("linkedinURL", e.target.value)
                  }
                  copyable
                />
              </div>
            </Col>

          </Row>

        </div>
      </div>
    </div>
  );
}
export default AgencyInformationForm;